import {
    Component, Input, ViewChild, OnDestroy, OnInit,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import SwiperCore, { Navigation, Scrollbar } from 'swiper';
import { SwiperComponent } from 'swiper/angular';

import { getCatalogStyles } from '../../../../../../store/category-page.selectors';
import { getSliderTemplateConfig } from '../../configs/slider-template-configs';
import { SliderTemplateConfig } from '../../models/slider-types-models';

import { CorePlatformService } from '@core/infrastructure/platform/platform.service';
import { getLanguageisRTL } from '@core/infrastructure/state/core.selectors';
import { ProductsArrayPublicSelectors } from '@features/products/productsArray';
import { ProductsArray } from '@features/products/productsArray/domain/models/productsArray.models';
import { Resolutions } from '@layout/domain/models/layout.models';
import { getResolution } from '@layout/infrastructure/state/layout.selectors';
import { CategoryConfiguration, GridProductStyle, HiddenFieldType } from '@shared/product/models';

// install Swiper modules
SwiperCore.use([Navigation]);
SwiperCore.use([Scrollbar]);

@Component({
    selector: 'studio-slider-template',
    templateUrl: './studio-slider-template.component.html',
})

export class StudioSliderTemplateComponent implements OnInit, OnDestroy {
    @ViewChild('swiper') public swiperComponent?: SwiperComponent;

    @Input() public products: number[];

    @Input() public categoryConfiguration: CategoryConfiguration;

    @Input() public templateName: string;

    @Input() public place: string;

    @Input() public elementIndex: number;

    @Input() public elementDefaultImageTypes: Map<number, string>;

    @Input() public productStyle: GridProductStyle;

    public templateNameFirst: string;

    public readonly uuid = Date.now().toString();

    public isMobile$: Observable<boolean> = this.store.select(getResolution)
        .pipe(map((resolution) => resolution === Resolutions.MOBILE));

    public desktopLarge$: Observable<boolean> = this.store.select(getResolution)
        .pipe(map((resolution) => resolution === Resolutions.DESKTOP_LARGE));

    public direction$: Observable<string> = this.store.select(getLanguageisRTL).pipe(map(isRtl => isRtl ? 'rtl' : 'ltr'));

    public slidesPerView: string;

    public navigationOptions = {

        nextEl: `.swiper-${this.uuid}-nav-right`,

        prevEl: `.swiper-${this.uuid}-nav-left`,
    };

    private readonly destroySubject$ = new Subject<void>();

    public productsLoaded: ProductsArray;

    public isBrowser = this.platform.isBrowser();

    public templateConfiguration$: Observable<SliderTemplateConfig>;

    public skeletonNoDetails: string;

    public catalogStyles: Record<string, string>;

    constructor(
        private readonly store: Store,
        private readonly platform: CorePlatformService,
    ) {}

    public ngOnInit(): void {
        this.templateNameFirst = this.templateName;
        this.getTemplatePrefix();
        this.templateConfiguration$ = this.store.select(getCatalogStyles).pipe(
            map((catalogStyles: Record<string, string>) => {
                this.catalogStyles = catalogStyles;
                const config = getSliderTemplateConfig(this.templateNameFirst);
                this.templateNameFirst = `swiper-template-${this.templateNameFirst.toLowerCase().replace('_', '-')}`;
                if (config.needReload) {
                    this.getProductsList();
                }
                return {
                    ...config,
                    templateConfigurations: {
                        ...config.templateConfigurations,
                        showSecondImageIfPossible: false,
                        styles: {
                            backgroundColor: catalogStyles?.backgroundColor,
                            fontColor: catalogStyles?.fontColor,
                            offerPrice: catalogStyles?.offerPrice,
                            colorSelector: catalogStyles?.colorSelector,
                            cardColor: catalogStyles?.backgroundColor,
                        },
                    },
                };
            })
        );

        const categoryConf = this.findCommonHiddenField();
        if (categoryConf) {
            this.setSkeletonNoDetails(categoryConf);
        }
    }

    public getProductsList(): void {
        this.store.select(ProductsArrayPublicSelectors.getProductsList)
            .pipe(takeUntil(this.destroySubject$))
            .subscribe((productsArray) => {
                this.productsLoaded = productsArray;

                if (this.isSwipperResetRequired(this.products, productsArray)) {
                    this.resetSwiper();
                }
            });
    }

    public ngOnDestroy(): void {
        this.destroySubject$.next();
        this.destroySubject$.complete();
    }

    private isSwipperResetRequired(currentProductIds: number[], productsArray: ProductsArray): boolean {
        if (!this.isBrowser) {
            return false;
        }

        const p = currentProductIds.filter((prod) => {
            return Object.keys(productsArray).includes(prod.toString());
        });

        return currentProductIds.length === p.length;
    }

    private resetSwiper(): void {
        setTimeout(() => {
            this.swiperComponent?.swiperRef?.destroy();
            this.swiperComponent?.initSwiper();
        }, 1000);
    }

    private findCommonHiddenField(): HiddenFieldType | undefined {
        let commonHiddenField: HiddenFieldType | undefined;

        for (const productId of this.products) {
            const productCategoryConf = this.categoryConfiguration?.productConf?.get(productId);

            if (commonHiddenField === undefined) {
                commonHiddenField = productCategoryConf?.hiddenField;
            } else if (productCategoryConf?.hiddenField !== commonHiddenField) {
                return undefined;
            }
        }

        return commonHiddenField;
    }

    private getTemplatePrefix(): void {
        const parts = this.templateName.split('_');
        this.templateNameFirst = parts.length > 1 ? parts[0] : this.templateName;
    }

    private setSkeletonNoDetails(hiddenField: HiddenFieldType): void {
        const skeletonMap = {
            all: 'sk-nodetails',
            description: 'sk-nodescription',
        };

        this.skeletonNoDetails = skeletonMap[hiddenField] || '';
    }
}
